import { useEffect, useRef, useState } from "react";
import "./GeminiChat.css";

/* Typing indicator */
function TypingDots() {
  return (
    <span className="gc-typing">
      <span></span><span></span><span></span>
    </span>
  );
}

/* A single message bubble with mini avatar */
function Msg({ role, children, onCopy }) {
  const isUser = role === "user";
  return (
    <div className={`gc-row ${isUser ? "gc-right" : "gc-left"}`}>
      <div className={`gc-mini ${isUser ? "user" : "bot"}`}>
        {isUser ? "👤" : "🤖"}
      </div>
      <div className={`gc-bubble ${isUser ? "gc-user" : "gc-bot"}`}>
        <div className="gc-text">{children}</div>
        {!isUser && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#94a3b8" }}>
            <button
              type="button"
              onClick={onCopy}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "inherit",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Copy
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function normalizeBase(url) {
  if (!url) return "";
  return String(url).replace(/\/+$/, "");
}

// ✅ Clipboard-safe copy (works even if navigator.clipboard is unavailable)
async function safeCopy(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {}

  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}

export default function GeminiChat({
  // ✅ CRA safe + Production safe: always same-origin (no localhost, no env needed)
  apiBase = typeof window !== "undefined" ? window.location.origin : "",
  onClose = () => {},
  autoGreet = true,
  greetingText = "👋 Hi! I’m your DIWASA Assistant. How may I help you today?",
  onOpenMap,
}) {
  const base = normalizeBase(apiBase);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState("");

  const endRef = useRef(null);
  const inputRef = useRef(null);
  const esRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    return () => {
      try {
        esRef.current?.close();
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (autoGreet && messages.length === 0) {
      setMessages([{ role: "assistant", content: greetingText }]);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGreet]);

  const appendAssistant = (text) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === "assistant") {
        return [
          ...prev.slice(0, -1),
          { role: "assistant", content: (last.content || "") + text },
        ];
      }
      return [...prev, { role: "assistant", content: text }];
    });
  };

  const handlePossibleAction = (json, replyText) => {
    let actionObj = null;

    if (json && json.action) actionObj = json.action;

    if (!actionObj && typeof replyText === "string") {
      const match = replyText.match(
        /\{[\s\S]*"action"\s*:\s*"open_portfolio_map"[\s\S]*\}/
      );
      if (match) {
        try {
          actionObj = JSON.parse(match[0]);
        } catch {}
      }
    }

    if (!actionObj) return;

    const type = actionObj.type || actionObj.action;
    if (type !== "open_portfolio_map") return;

    const category = actionObj.category || actionObj.mapCategory || "Country";
    const region = actionObj.region || actionObj.country || actionObj.basin;

    if (!region) return;
    if (typeof onOpenMap === "function") onOpenMap({ category, region });
  };

  // ✅ Only send user turns to backend
  const buildUserHistory = (history) =>
    history
      .filter((m) => m.role === "user")
      .map((m) => ({ role: "user", content: m.content }));

  const nonStreamCall = async (text, nextHistory) => {
    setStatus("contacting server…");

    const userHistory = buildUserHistory(nextHistory);

    // ✅ same-origin api call
    const res = await fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: text, history: userHistory }),
    });

    const contentType = res.headers.get("content-type") || "";
    if (!res.ok) {
      let details = "";
      if (contentType.includes("application/json")) {
        const j = await res.json().catch(() => null);
        details = j?.details || j?.error || JSON.stringify(j || {});
      } else {
        details = await res.text().catch(() => "");
      }
      throw new Error(`Server ${res.status}: ${details || "unknown error"}`);
    }

    const json = await res.json();
    const replyText = json?.reply || "…";

    appendAssistant(replyText);
    handlePossibleAction(json, replyText);
    setStatus("");
  };

  const streamCall = async (text, nextHistory) => {
    setStatus("streaming…");

    const userHistory = buildUserHistory(nextHistory);

    try {
      esRef.current?.close();
    } catch {}

    const url =
      `${base}/api/chat/stream` +
      `?message=${encodeURIComponent(text)}` +
      `&history=${encodeURIComponent(JSON.stringify(userHistory))}`;

    appendAssistant("");

    const es = new EventSource(url);
    esRef.current = es;

    return await new Promise((resolve, reject) => {
      let finalText = "";

      es.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);

          if (data.token) {
            finalText += data.token;
            appendAssistant(data.token);
          }

          if (data.done) {
            es.close();
            esRef.current = null;
            handlePossibleAction(null, finalText);
            setStatus("");
            resolve();
          }

          if (data.error) {
            es.close();
            esRef.current = null;
            setStatus("");
            reject(new Error(String(data.error)));
          }
        } catch {
          finalText += evt.data;
          appendAssistant(evt.data);
        }
      };

      es.onerror = () => {
        try {
          es.close();
        } catch {}
        esRef.current = null;
        setStatus("");
        reject(new Error("Streaming failed. Check /api/chat/stream on server."));
      };
    });
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;

    setInput("");
    setStatus("");

    const nextHistory = [...messages, { role: "user", content: text }];
    setMessages(nextHistory);

    try {
      setLoading(true);
      if (streaming) await streamCall(text, nextHistory);
      else await nonStreamCall(text, nextHistory);
    } catch (e) {
      appendAssistant(`Error: ${String(e.message || e)}`);
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clear = () => setMessages([]);
  const regen = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "user") {
        send(messages[i].content);
        break;
      }
    }
  };

  const hasUserMessage = messages.some((m) => m.role === "user");

  const quickQuestions = [
    "How do I request data?",
    "What datasets are available?",
    "Open the interactive map",
    "Show the portfolio for Ethiopia",
    "River basin assessments",
    "Contact DIWASA team",
    "How to access national water accounts?",
    "Show data products list",
  ];

  return (
    <div className="gc-panel">
      {/* Header */}
      <div className="gc-header">
        <div className="gc-brand">
          <div className="gc-avatar">AI</div>
          <div>
            <div className="gc-title">DIWASA Assistant</div>
            <div className="gc-subtitle">
              <span className="gc-badge-online">Online</span>
              <span className="gc-sep">•</span>
            </div>
          </div>
        </div>

        <div className="gc-controls">
          <label className="gc-toggle">
            <input
              type="checkbox"
              checked={streaming}
              onChange={(e) => setStreaming(e.target.checked)}
            />
            <span>Streaming</span>
          </label>

          {status && <span className="gc-status">{status}</span>}

          <button className="gc-close" type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="gc-body">
        {messages.map((m, i) => (
          <Msg key={i} role={m.role} onCopy={() => safeCopy(m.content || "")}>
            {m.content}
          </Msg>
        ))}

        {!hasUserMessage && (
          <div className="gc-chips">
            {quickQuestions.map((t) => (
              <button key={t} className="gc-chip" onClick={() => send(t)}>
                {t}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div style={{ marginLeft: 42, marginTop: 4 }}>
            <TypingDots />
          </div>
        )}

        <div ref={endRef} />
      </div>

      {/* Toolbar */}
      <div className="gc-toolbar">
        <div className="gc-status">{status}</div>
        <div className="gc-actions">
          <button className="gc-btn" onClick={clear}>
            Clear
          </button>
          <button className="gc-btn" onClick={regen}>
            Regenerate
          </button>
          {loading && (
            <button
              className="gc-btn gc-danger"
              onClick={() => {
                try {
                  esRef.current?.close();
                } catch {}
                setLoading(false);
              }}
            >
              Stop
            </button>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="gc-composer">
        <input
          ref={inputRef}
          className="gc-input"
          placeholder="How may I help you?"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
        />
        <button
          className="gc-send"
          type="button"
          disabled={loading || !input.trim()}
          onClick={() => send()}
        >
          Send
        </button>
      </div>
    </div>
  );
}
